import React, { useState, useEffect } from "react";
import { Container, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Pagination, Grid, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BASE_URL from "../config";

// Styled Table Components
const StyledTableContainer = styled(TableContainer)({
  marginTop: "20px",
  borderRadius: "10px",
  overflowX: "auto",
  boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: "#1976d2",
  "& th": {
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
  },
});

const StyledTableRow = styled(TableRow)({
  "&:nth-of-type(odd)": {
    backgroundColor: "#f9f9f9",
  },
  "&:hover": {
    backgroundColor: "#e3f2fd",
  },
});

const FeeListPage = () => {
  const [courses, setCourses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [searchText, setSearchText] = useState("");
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${BASE_URL}/api/coursesa`).then((res) => setCourses(res.data));
  }, []);

  useEffect(() => {
    if (selectedCourse) {
      axios.get(`${BASE_URL}/api/academicYears/${selectedCourse}`).then((res) => {
        setAcademicYears(res.data);
        const availableYearIDs = res.data.map(y => y.AcademicYearID);
        if (!availableYearIDs.includes(parseInt(selectedYear))) {
          setSelectedYear("");
        }
      });

      axios.get(`${BASE_URL}/api/classes?course=${selectedCourse}`).then((res) => {
        setClasses(res.data);
        const availableClassIDs = res.data.map(c => c.id || c.ClassID);
        if (!availableClassIDs.includes(parseInt(selectedClass))) {
          setSelectedClass("");
        }
      });
    } else {
      setAcademicYears([]);
      setClasses([]);
      setSelectedYear("");
      setSelectedClass("");
    }
  }, [selectedCourse]);

  useEffect(() => {
    if (selectedCourse && selectedYear && selectedClass) {
      handleSearch();
    }
  }, [page]);

  const handleSearch = () => {
    if (!selectedCourse || !selectedYear || !selectedClass) {
      alert("Please select Course, Academic Year, and Class before searching.");
      return;
    }

    axios.get(`${BASE_URL}/api/students`, {
      params: {
        courseId: selectedCourse,
        yearId: selectedYear,
        classId: selectedClass,
        search: searchText,
        page: page,
      }
    })
    .then((res) => {
      setStudents(res.data.students);
      setTotalPages(res.data.totalPages);
    })
    .catch((err) => console.error("Error fetching students:", err));
  };

  const handleCourseChange = (e) => {
    const courseID = e.target.value;
    setSelectedCourse(courseID);
    setSelectedYear("");
    setSelectedClass("");
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" sx={{ marginBottom: 2, textAlign: "center", fontWeight: "bold", color: "#1976d2" }}>
        Fee List
      </Typography>

      {/* Filters Section */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            label="Course"
            value={selectedCourse || ""}
            onChange={handleCourseChange}
            fullWidth
          >
            <MenuItem value="">-- Select Course --</MenuItem>
            {courses.map((course) => (
              <MenuItem key={course.CourseID} value={course.CourseID}>
                {course.CourseName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select
            label="Academic Year"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(e.target.value)}
            fullWidth
            disabled={!selectedCourse || academicYears.length === 0}
          >
            <MenuItem value="">-- Select Academic Year --</MenuItem>
            {academicYears.map((year) => (
              <MenuItem key={year.AcademicYearID} value={year.AcademicYearID}>
                {year.AcademicYearName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            select
            label="Class"
            value={selectedClass || ""}
            onChange={(e) => setSelectedClass(e.target.value)}
            fullWidth
            disabled={!selectedCourse || classes.length === 0}
          >
            <MenuItem value="">-- Select Class --</MenuItem>
            {classes.map((cls) => (
              <MenuItem key={cls.id || cls.ClassID} value={cls.id || cls.ClassID}>
                {cls.name || cls.ClassName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Search Field */}
      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        <Grid item xs={9}>
          <TextField
            label="Search Student"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={3}>
          <Button variant="contained" onClick={handleSearch} fullWidth sx={{ height: "100%" }}>
            Search
          </Button>
        </Grid>
      </Grid>

      {/* Student List Table */}
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell>Academic Year</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Class</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {students.length > 0 ? (
              students.map((student) => (
                <StyledTableRow key={student.StudentID}>
                  <TableCell align="center">{student.AcademicYearName}</TableCell>
                  <TableCell align="center">
                    <Button
                      onClick={() => {
                        const paymentData = {
                          courseId: selectedCourse,
                          academicYearId: selectedYear,
                          classId: selectedClass,
                        };
                        localStorage.setItem("paymentDetails1", JSON.stringify(paymentData));
                        localStorage.setItem("paymentDetails", JSON.stringify(student));
                        navigate(`/PaymentPage/${student.StudentID}`);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      {student.StudentName}
                    </Button>
                  </TableCell>
                  <TableCell align="center">{student.CourseName}</TableCell>
                  <TableCell align="center">{student.ClassName}</TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ padding: "20px", fontStyle: "italic" }}>
                  No students found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Pagination */}
      <Pagination
        count={totalPages}
        page={page}
        onChange={(e, value) => setPage(value)}
        sx={{ marginTop: 2, display: "flex", justifyContent: "center" }}
      />
    </Container>
  );
};

export default FeeListPage;
